// The cinematic reveal, for the gems (and a first on the site of Silver and Gold):
// a dark stage framed 9:16 (a screen recording is a ready story), lit in the
// tier's colour. The badge arrives sealed, a shaking silhouette with the tier's
// light leaking round it. A tap charges it, it bursts open, the layers assemble,
// and after a beat the tier line and then the name land. A first on the site then
// gets its stamp slammed on, a second burst of gold light and a ring of crowns.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BadgeArt from "../art/BadgeArt";
import { tierStyle } from "../art/badgeLib";
import type { UnrevealedCopy } from "../types";
import { haptic } from "../../../utils/haptics";
import { useReducedMotion } from "./hooks";
import { useModalBehaviour } from "./overlay";
import RevealBurst from "./RevealBurst";
import { pointsLabel, repeatLabel, revealLine } from "./revealQueue";
import { tierLineText } from "../collection/badgeFormat";
import { TierLineView } from "../TierName";
import { CrownRing, FirstStamp } from "./FirstEver";
import { valueOf } from "../value";
import { FIRST_GOLD, STAMP_IMPACT_MS, cinematicTimeline, hexAlpha, particleCount, stageLight } from "./timeline";

export interface RevealActions {
  onCollect: () => void;
  /** Resolves once the share page is open; rejects when no link could be made. */
  onShare: () => Promise<void>;
  onSeeCollection: () => void;
  /**
   * "Skip the other silver (4)": collect this reveal and every queued reveal of
   * the same tier. Left out when no other reveal of the tier is queued.
   */
  skipTier?: { label: string; onSkip: () => void };
}

export interface BadgeCinematicProps extends RevealActions {
  copy: UnrevealedCopy;
  /** Cinematics and cards queued after this one. */
  waiting?: number;
  /** Fading out after Collect. */
  closing?: boolean;
}

type Phase = "sealed" | "charging" | "revealed";

/** 0 nothing, 1 tier line, 2 name, 3 description and count, 4 actions */
type Shown = 0 | 1 | 2 | 3 | 4;

const GOLD_BURST = [FIRST_GOLD.fill, FIRST_GOLD.gloss, "#ffffff", FIRST_GOLD.deep];

const BadgeCinematic: React.FC<BadgeCinematicProps> = ({ copy, waiting = 0, closing = false, onCollect, onShare, onSeeCollection, skipTier }) => {
  const reduced = useReducedMotion();
  const [phase, setPhase] = useState<Phase>("sealed");
  const [shown, setShown] = useState<Shown>(0);
  const [burstOn, setBurstOn] = useState(false);
  const [promptOn, setPromptOn] = useState(false);
  // once the layers have assembled the badge settles into its idle motion
  const [artIdle, setArtIdle] = useState(false);
  // first on the site: the stamp, then its impact (gold burst, crowns, a thud)
  const [stampOn, setStampOn] = useState(false);
  const [impact, setImpact] = useState(false);
  const [shareState, setShareState] = useState<"idle" | "busy" | "failed">("idle");
  const rootRef = useRef<HTMLDivElement>(null);
  const timers = useRef<number[]>([]);

  const line = revealLine(copy);
  const first = !!copy.firstEver;
  const tierKey = String(copy.tier).toLowerCase();
  const ordinal = copy.repeatIndex > 1 ? copy.repeatIndex : copy.count > 1 ? copy.count : 1;
  const repeat = repeatLabel(ordinal);
  // this copy's worth: a repeat is worth 4.5% less than the copy before it
  const value = valueOf(copy);
  // the tier's colour is the rarity: it leaks round the seal and lights the stage
  const light = stageLight(copy.tier);
  const color = light;

  const later = useCallback((ms: number, fn: () => void) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    },
    []
  );

  // "Tap to reveal" fades in once the silhouette has settled. Focus goes to the
  // dialog itself (Enter or Space there reveals); a keyboard member can also tab
  // to the seal, and the prompt lights up when it has focus.
  useEffect(() => {
    const t = window.setTimeout(() => setPromptOn(true), reduced ? 0 : 900);
    rootRef.current?.focus({ preventScroll: true });
    return () => window.clearTimeout(t);
  }, [reduced]);

  const reveal = useCallback(() => {
    if (phase !== "sealed") return;
    const tl = cinematicTimeline(reduced, first);
    haptic("medium");
    if (reduced) {
      setPhase("revealed");
    } else {
      setPhase("charging");
      later(tl.burst, () => {
        setPhase("revealed");
        setBurstOn(true);
        haptic("success");
      });
    }
    later(tl.rarity, () => {
      setShown((s) => (s < 1 ? 1 : s));
      setArtIdle(true);
    });
    later(tl.name, () => setShown((s) => (s < 2 ? 2 : s)));
    if (tl.stamp !== null) {
      const stampAt = tl.stamp;
      later(stampAt, () => setStampOn(true));
      if (!reduced) {
        later(stampAt + STAMP_IMPACT_MS, () => {
          setImpact(true);
          haptic("heavy");
        });
      }
    }
    later(tl.details, () => setShown((s) => (s < 3 ? 3 : s)));
    later(tl.actions, () => setShown(4));
  }, [phase, reduced, first, later]);

  // a tap on the stage while the words are still coming shows them all (and the
  // stamp; the gold burst is not replayed out of turn)
  const skip = useCallback(() => {
    if (phase === "revealed" && shown < 4) {
      setShown(4);
      if (first) setStampOn(true);
    }
  }, [phase, shown, first]);

  const onEscape = useCallback(() => {
    if (phase === "sealed") return;
    if (shown < 4) skip();
    else onCollect();
  }, [phase, shown, skip, onCollect]);
  useModalBehaviour(rootRef, onEscape);

  const share = async () => {
    if (shareState === "busy") return;
    setShareState("busy");
    try {
      await onShare();
      setShareState("idle");
    } catch {
      setShareState("failed");
    }
  };

  const vars = useMemo(
    () =>
      ({
        "--rarity": color,
        "--rarity-a": hexAlpha(color, 0.55),
        "--rarity-soft": hexAlpha(color, 0.2),
        "--light": light,
        "--light-a": hexAlpha(light, 0.5),
        "--light-soft": hexAlpha(light, 0.14),
      }) as React.CSSProperties,
    [color, light]
  );

  const burstColors = useMemo(() => {
    const t = tierStyle(copy.tier);
    return [light, t.fill, "#ffffff", t.gloss];
  }, [light, copy.tier]);
  const revealed = phase === "revealed";

  const classes = [
    "bdg-cine",
    `bdg-cine--${phase}`,
    `bdg-cine--${tierKey}`,
    first ? "bdg-cine--first" : "",
    impact ? "bdg-cine--stamped" : "",
    reduced ? "bdg-cine--reduced" : "",
    shown >= 4 ? "bdg-cine--done" : "",
    closing ? "is-closing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const node = (
    <div
      ref={rootRef}
      className={classes}
      role="dialog"
      aria-modal="true"
      aria-label={revealed ? `New badge: ${copy.name}` : "New badge"}
      tabIndex={-1}
      style={vars}
      data-testid="badge-cinematic"
      data-phase={phase}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
        e.preventDefault();
        if (phase === "sealed") reveal();
        else skip();
      }}
    >
      {/* the whole frame is the tap target; the buttons stop the click */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="bdg-cine__frame" onClick={phase === "sealed" ? reveal : skip}>
        <div className="bdg-cine__light" />
        <div className="bdg-cine__leak" />
        <div className="bdg-cine__top">
          <span className="bdg-cine__mark">n450s.com</span>
          <span className="bdg-cine__kicker">
            New badge{waiting > 0 ? <span className="bdg-cine__waiting"> · {waiting} more after this</span> : null}
          </span>
        </div>

        <div className="bdg-cine__center">
          <div className="bdg-cine__stage">
            <div className="bdg-cine__rays" />
            <div className="bdg-cine__corona" />
            {burstOn && !reduced && (
              <RevealBurst
                seed={copy.copyId}
                count={particleCount("cinematic", copy.tier)}
                spread={190}
                colors={burstColors}
                ringColor={color}
                flash
              />
            )}
            {first && (impact || (reduced && stampOn)) && <CrownRing />}
            {impact && !reduced && (
              <RevealBurst seed={`${copy.copyId}:first`} count={30} spread={230} colors={GOLD_BURST} ringColor={FIRST_GOLD.fill} flash variant="gold" />
            )}
            <button
              type="button"
              className="bdg-cine__seal"
              onClick={(e) => {
                e.stopPropagation();
                if (phase === "sealed") reveal();
                else skip();
              }}
              aria-label={phase === "sealed" ? "Reveal your new badge" : copy.name}
              tabIndex={phase === "sealed" ? 0 : -1}
            >
              <BadgeArt
                category={copy.category}
                tier={copy.tier}
                number={copy.number}
                code={copy.code}
                name={copy.name}
                count={revealed && ordinal > 1 ? ordinal : undefined}
                size={280}
                variant="full"
                sealed={!revealed}
                animate={!revealed ? "none" : artIdle ? "idle" : "reveal"}
                revealKey={revealed ? "open" : "sealed"}
                className="bdg-cine__art"
              />
            </button>
            <div className="bdg-cine__floor" />
            {stampOn && <FirstStamp tier={copy.tier} reduced={reduced} className="bdg-cine__stamp" />}
          </div>

          <div className="bdg-cine__words">
            <p className={`bdg-cine__rarity${shown >= 1 ? " is-in" : ""}`} data-testid="badge-rarity-line">
              <TierLineView line={line} className="bdg-tierline--stage" />
            </p>
            <h2 className={`bdg-cine__name${shown >= 2 ? " is-in" : ""}`}>{copy.name}</h2>
            <div className={`bdg-cine__details${shown >= 3 ? " is-in" : ""}`}>
              {repeat && <span className="bdg-cine__repeat">{repeat}</span>}
              {copy.description && <p className="bdg-cine__desc">{copy.description}</p>}
              {value > 0 && <p className="bdg-cine__points">{pointsLabel(value)}</p>}
            </div>
          </div>
        </div>

        <div className="bdg-cine__bottom">
          {phase === "sealed" && <p className={`bdg-cine__prompt${promptOn ? " is-in" : ""}`}>Tap to reveal</p>}
          {revealed && (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div className={`bdg-cine__actions${shown >= 4 ? " is-in" : ""}`} onClick={(e) => e.stopPropagation()}>
              <button type="button" className="bdg-btn bdg-btn--stage-primary" onClick={onCollect} disabled={shown < 4}>
                Collect
              </button>
              <div className="bdg-cine__row">
                <button type="button" className="bdg-btn bdg-btn--stage" onClick={share} disabled={shown < 4 || shareState === "busy"}>
                  {shareState === "busy" ? "Making a link" : "Share"}
                </button>
                <button type="button" className="bdg-btn bdg-btn--stage" onClick={onSeeCollection} disabled={shown < 4}>
                  See collection
                </button>
              </div>
              {skipTier && (
                <button type="button" className="bdg-skip-tier bdg-skip-tier--stage" onClick={skipTier.onSkip} disabled={shown < 4}>
                  {skipTier.label}
                </button>
              )}
              {shareState === "failed" && <p className="bdg-cine__error">Could not make a share link. Try again.</p>}
            </div>
          )}
        </div>
      </div>
      <p className="bdg-sr" aria-live="polite">
        {shown >= 2 ? `${copy.name}. ${tierLineText(line)}` : ""}
      </p>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(node, document.body) : node;
};

export default BadgeCinematic;
